import { useEffect, useState } from "react";
import { RefreshControl, TouchableOpacity, View } from "react-native";
import { Query } from "@/utils/react-query/Queries";
import { QueryKeys } from "@/utils/react-query/QueryKeys";
import { LegendList } from "@legendapp/list";
import EmptyListPlaceholder from "@/components/General/EmptyListPlaceholder";
import SearchBox from "@/components/General/inputs/SearchBox";
import { ActivityIndicator } from "react-native-paper";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import LinkPreview from "@/components/Library/LinkPreview";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { deleteLink } from "@/data/library";
import { showAlert } from "@/utils/showAlert";
import { useRouter, useNavigation } from "expo-router";
import DropdownMenu, {dropdownIcons} from "@/components/General/DropdownMenu";
import { useUnistyles } from "react-native-unistyles";
import Unicon from "@/components/General/Unicon";


export default function LinksScreen(){
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState(null);

    /*** Theme & Dimensions ***/
    const {theme: {colors}} = useUnistyles();
    const router = useRouter();
    const navigation = useNavigation();

    /*** Queries ***/
    const queryClient = useQueryClient()
    const {data: links, isFetching} = Query.links.all();
    const {data: loggedInUser} = Query.people.loggedInUser()

    const deleteLinkMutation = useMutation({
        mutationFn: deleteLink,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.links.all()});
        }
    })

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => (
                loggedInUser?.can?.("Create Links") && (
                    <View style={{flexDirection:"row"}}>
                        <TouchableOpacity onPress={() => router.push("/library/links/create")}>
                            <Unicon name="add" size={28} colorVariant="text" />
                        </TouchableOpacity>
                    </View>
                )
            ),
        });
    }, [navigation, loggedInUser])

    useEffect(() => {
        if(links && links.length > 0){
            filterLinks();
        }
    }, [links, searchText])


    const filterLinks = async () => {
        let storedOGInfo = await AsyncStorage.getItem("ogInfo");

        if(storedOGInfo){
            storedOGInfo = JSON.parse(storedOGInfo);
        }

        const list = links.filter((l) => {
            const thisLinkOGInfo = storedOGInfo?.[l.url] || {};

            const name = !l.name ? thisLinkOGInfo?.ogTags?.["og:title"] : l.name;
            const description = !l.description ? thisLinkOGInfo?.ogTags?.["og:description"] : l.description;
            const url = !l.url ? thisLinkOGInfo?.ogTags?.["og:url"] : l.url;

            if(searchText != "" && (
                name?.toLowerCase().indexOf(searchText.toLowerCase()) === -1 && 
                description?.toLowerCase().indexOf(searchText.toLowerCase()) === -1 &&
                url?.toLowerCase().indexOf(searchText.toLowerCase()) === -1 )){
                    return false;
                }
            
            return true;
        })

        setData(list);
    }


    const refreshList = () => {
        queryClient.invalidateQueries({queryKey: QueryKeys.links.all()});
    }

    const renderItem = ({ item }) => (
        <View style={{marginBottom:20}}>
            <View style={{position:"absolute", right:10, top:4, zIndex:10}}>
                <DropdownMenu options={[
                    {name: "Edit Link", icon: dropdownIcons.edit, onSelect: () => router.push({ pathname: "/library/links/create", params: {id: item.id} })},
                    {name: "Delete Link", destructive: true, icon: dropdownIcons.delete, onSelect: () => {
                        showAlert({
                            title: "Delete Link",
                            subtitle: "Are you sure you want to delete this link?",
                            isDestructive: true,
                            onConfirm: () => deleteLinkMutation.mutate({id: item.id}),
                            confirmText: "Delete"
                        })
                    }}
                ]} />
            </View>
            <LinkPreview url={item.url} linkObject={item} key={item.url} />
        </View>
    );

    const header = (
        <View style={{padding:10}}>
            <SearchBox
                searchText={searchText}
                onChangeText={(text) => {
                    setSearchText(text);
                }}
                textChangeDelay={0}
            />
        </View>
    )

    return (
        <View style={{flex:1, alignItems:"center"}}>
            <View style={{flex:1, width:"100%", maxWidth:700}}>
                {header}
                <LegendList recycleItems keyExtractor={(item) => item.id} 
                    showsVerticalScrollIndicator={false}
                    estimatedItemSize={80}
                    removeClippedSubviews
                    contentContainerStyle={{ paddingHorizontal:10}}
                    data={isFetching ? [] : (!data ? links : data)}
                    refreshControl={<RefreshControl refreshing={false} progressViewOffset={1000} onRefresh={refreshList}/>}
                    renderItem={renderItem}
                    ListFooterComponent={!isFetching 
                        ? links?.length == 0 ? <EmptyListPlaceholder spacing={10} message="No links available, create a new link with the '+' icon!"/> : <View style={{paddingBottom:30}}></View>
                        : <View style={{alignContent:'center', justifyContent:'center'}}>
                           <ActivityIndicator size="large" color={colors.primary}/>
                        </View>}
                    keyboardShouldPersistTaps="handled"
                />

                
            </View>
        </View>
    );
};
